import React, { useState, useRef, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

// Hooks y Servicios
import { useRoom } from '../../../hooks/useRoom';

// Estilos y Skeletons
import './RoomDetails.css';
import RoomDetailSkeleton from './RoomDetailSkeleton';

// Componentes UI
import ReserveButton from '../../atoms/ReserveButton/ReserveButton';
import OtherRoomsSection from '../../organisms/OtherRoomsSection/OtherRoomsSection';
import CompareRoomsSection from '../../organisms/CompareRoomsSection/CompareRoomsSection';

interface BookingContext {
    openBooking: () => void;
}

const RoomDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { i18n } = useTranslation();

    const { openBooking } = useOutletContext<BookingContext>();
    const { room, loading, error } = useRoom(slug);

    const [currentImgIndex, setCurrentImgIndex] = useState(0);
    const animatedElementsRef = useRef<HTMLDivElement[]>([]);

    useEffect(() => { setCurrentImgIndex(0) }, [slug]);

    useEffect(() => {
        if (loading) return;
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.2 });

        animatedElementsRef.current.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [loading, room]);

    const addToRefs = (el: any) => {
        if (el && !animatedElementsRef.current.includes(el)) animatedElementsRef.current.push(el);
    };

    const nextImage = () => room && setCurrentImgIndex(prev => (prev === room.images.length - 1 ? 0 : prev + 1));
    const prevImage = () => room && setCurrentImgIndex(prev => (prev === 0 ? room.images.length - 1 : prev - 1));

    if (loading) return <RoomDetailSkeleton />;
    if (error || !room) return <div className="room-page-error">Habitación no encontrada</div>;

    const heroImage = room.images[0]?.url || '';
    const galleryImage = room.images[currentImgIndex]?.url || '';
    const isEs = i18n.language.startsWith('es');

    const texts = {
        amenitiesTitle: isEs ? 'AMENIDADES' : 'AMENITIES',
        capacityLabel: isEs ? 'Capacidad máxima:' : 'Maximum capacity:',
        guestLabel: isEs ? 'personas' : 'guests',
        breakfastText: isEs ? 'Incluye desayuno a la carta' : 'Includes a la carte breakfast',
        // Eliminados labels de superficie/distancia porque ya vienen integrados en el nombre de la amenidad
    };

    return (
        <div className="room-page-container">
            {/* 1. HERO HEADER */}
            <header className="room-hero" style={{ backgroundImage: `url('${heroImage}')` }}>
                <div className="room-hero-overlay"></div>
                <h1 className="room-hero-title animate" ref={addToRefs}>{room.title}</h1>
            </header>

            <div className="room-content-container">
                <h2 className="room-title animate" ref={addToRefs}>{room.title}</h2>

                {/* DESCRIPCIÓN */}
                <div className="room-description animate" ref={addToRefs}>
                    <ReactMarkdown>
                        {room.description}
                    </ReactMarkdown>
                </div>

                <div className="room-details-grid animate" ref={addToRefs}>
                    {/* 2. COLUMNA IZQUIERDA: Galería */}
                    <div className="room-gallery-column">
                        <div className="room-gallery">
                            <img src={galleryImage} alt={room.title} className="room-main-image" />

                            {room.images.length > 1 && (
                                <>
                                    <div className="gallery-arrow left" onClick={prevImage}>‹</div>
                                    <div className="gallery-arrow right" onClick={nextImage}>›</div>
                                </>
                            )}
                        </div>

                        {room.room_360_tour && (
                            <div className="room-360-footer-container">
                                <a
                                    href={room.room_360_tour}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-360-tour-style"
                                >
                                    360° ROOM TOUR
                                </a>
                            </div>
                        )}
                    </div>

                    {/* 3. COLUMNA DERECHA: Amenidades (ESTRICTAS DEL BACKEND) */}
                    <div className="room-amenities">
                        <h3 className="amenities-title separator-bottom">
                            {texts.amenitiesTitle}
                        </h3>

                        <ul className="amenities-list">
                            {/* AQUI ESTÁ EL CAMBIO:
                                Ya no insertamos manualmente superficie, distancia o cama.
                                Iteramos DIRECTAMENTE room.amenities, confiando en que el backend
                                ya nos envió todo en el orden correcto (1, 2, 3...).
                            */}
                            {room.amenities.map((amenity) => (
                                <li key={amenity.id}>
                                    {/* Renderizamos el icono solo si existe */}
                                    {amenity.icon && (
                                        <i className={`fa-solid ${amenity.icon} amenity-icon`}></i>
                                    )}
                                    <span>{amenity.name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* 4. FOOTER (Barra de Reserva) - Aquí sí usamos specs para el resumen rápido */}
            <div className="booking-bar animate" ref={addToRefs}>
                <div className="booking-info text-center">
                    <p className="capacity-text" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {texts.capacityLabel} {room.specs.capacity} {texts.guestLabel}
                    </p>

                    <p className="details-text" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                        {room.specs.location}
                        {room.specs.breakfast_included && (
                            <>
                                <span className="separator mx-2"> | </span>
                                {texts.breakfastText}
                            </>
                        )}
                    </p>
                </div>

                <div
                    className="booking-bar-button"
                    onClick={openBooking}
                    style={{ cursor: 'pointer' }}
                >
                    <ReserveButton variant="light" />
                </div>
            </div>

            <OtherRoomsSection currentRoomId={room.slug} />
            <CompareRoomsSection />
        </div>
    );
};

export default RoomDetail;